/**
 * The vocabularies the UI is allowed to render, and nothing else.
 *
 * Every list here is a copy of a database CHECK constraint or of a
 * design-handoff decision. Where the two disagree the database wins
 * (CLAUDE.md), and the divergence is called out in a comment rather than
 * quietly reconciled — a UI that renders a value the database rejects is a
 * form that cannot be submitted.
 */

/**
 * `user_role.role` — six values, not the five the design handoff names.
 *
 * The handoff's README and BUILD_SPEC section 2 both say "only five roles exist
 * in `user_role`: admin · manager · mechanic · requester · viewer". The
 * committed schema disagrees: `packages/db/src/schema/core/enums.ts` carries a
 * sixth, `processor`, and its own comment says "`processor` is Matt's role —
 * takes the report, picks the vendor, chases them, enters the costs". The
 * seeded Roadside pipeline confirms it: every vendor-stage transition allows
 * `{processor, manager, admin}` and NOT `mechanic`.
 *
 * So the handoff's persona map (Matt -> manager) would give Matt a role that
 * *can* drive the vendor pipeline, but for the wrong reason, and would leave
 * `processor` — a real role with real grants — unreachable from the UI. Matt
 * maps to `processor` here. Flagged for Eden rather than decided silently.
 */
export const userRoles = [
  "admin",
  "manager",
  "mechanic",
  "processor",
  "requester",
  "viewer",
] as const;
export type UserRole = (typeof userRoles)[number];

/** `work_order.priority` CHECK. */
export const priorities = ["low", "normal", "high", "urgent"] as const;

/**
 * Priority as a SORT KEY, strongest first — the one definition (TRBUD-223).
 *
 * Ordering the column lexically would look almost right — `high` < `low` <
 * `normal` — and put `urgent` last, which is the single worst place for it. This
 * existed as a private `PRIORITY_RANK` in `apps/api/src/read/work-orders.ts` and
 * as a hand-written `CASE` in the same file's SQL; the queue sorts on it in two
 * places and both have to agree, so it is published here and the SQL `CASE` is
 * generated from it.
 */
export const priorityRank: Record<(typeof priorities)[number], number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};
export type Priority = (typeof priorities)[number];

/**
 * Display labels. Note that `normal` reads "Med" — the design's intake controls
 * offer Low / Med / High and the schema's middle value is `normal`, so the label
 * bridges the two rather than either side being renamed.
 *
 * Open item: the intake dialogs offer three of these four. Nothing created
 * through the UI can be `urgent`, which is either correct (urgency is expressed by
 * the roadside pipeline and a phone call) or an omission. Flagged for Eden.
 */
export const priorityLabels: Record<Priority, string> = {
  low: "Low",
  normal: "Med",
  high: "High",
  urgent: "Urgent",
};

/** `work_order.cost_status` CHECK. */
export const costStatuses = ["complete", "awaiting_final_cost"] as const;
export type CostStatus = (typeof costStatuses)[number];

/**
 * `work_order_comment.visibility` CHECK — exactly two values.
 *
 * DECISION B1 is open: there is no ops/requester tier. Do not add a third
 * option to a select; the column would reject it.
 */
export const visibilities = ["internal_only", "vendor_visible"] as const;
export type Visibility = (typeof visibilities)[number];

/** `work_order_time_entry.time_kind` CHECK. */
export const timeKinds = ["repair_labour", "admin_processing"] as const;
export type TimeKind = (typeof timeKinds)[number];

/** `cost_entry.cost_kind` CHECK — recovery kinds carry a negative amount. */
export const costKinds = [
  "part",
  "labour_internal",
  "labour_vendor",
  "tax",
  "shipping",
  "core_charge",
  "discount",
  "credit",
  "rebate",
  "refund",
  "warranty_recovery",
  "adjustment",
  "reversal",
] as const;
export type CostKind = (typeof costKinds)[number];

/**
 * The three kinds the "Add cost / credit" dialog offers, mapped to `cost_kind`.
 *
 * **`labour_internal` is deliberately absent, and so is `reversal`.**
 * `post_manual_cost` opens by rejecting both with
 * *'cost kind requires its dedicated posting function'*, and that refusal is
 * correct: a `labour_internal` row must carry `time_entry_id` + `labour_rate_id`
 * + `posted_rate`, with a unique index on `time_entry_id` stopping the same time
 * entry being billed twice — the chain the Labor ROI card reads. Internal labour
 * is therefore created by `post_time_entry` (the **Log time** dialog), and a
 * reversal by `reverse_cost_entry`.
 *
 * This list previously included `labour_internal`, so the dialog rendered a Labor
 * button whose every press returned a 23514 the user could do nothing about. Do
 * not add either kind back to make the menu look symmetrical with `costKinds` —
 * the asymmetry is the database's, and it is load-bearing.
 */
export const addCostKinds = ["part", "labour_vendor", "credit"] as const;
export type AddCostKind = (typeof addCostKinds)[number];

/**
 * The currency money is recorded in: USD, and only USD. Eden's ruling of
 * 5 October 2026 (TRBUD-385), narrowing the three of 8 September.
 *
 * A receipt paid in pesos or Canadian dollars is logged at the USD amount
 * actually charged on the card or bank statement, and the receipt's own
 * currency and amount are kept as a REFERENCE on the invoice
 * (`receiptReferenceCurrencies` below). TruckBuddy does not convert
 * currencies: there is no rate table anywhere in this schema.
 *
 * Every body schema that takes a money currency goes through `currencyCode` in
 * `requests.ts`, which reads this list, so a route refuses MXN and CAD before
 * the database does.
 *
 * Mirrored by the CHECK constraints migration `20261013162703_usd_only_money`
 * puts on `cost_entry`, `labour_rate`, `payment`, `purchase_order`,
 * `vendor_invoice`, `proposed_cost_line` and `custom_field_def`. The database
 * wins; this list is the copy. Do not add a code to make an integration fit.
 */
export const currencies = ["USD"] as const;
export type Currency = (typeof currencies)[number];

/**
 * The codes a money row may still CARRY, as opposed to the one a new row may
 * be written in.
 *
 * Production held no MXN or CAD row when the USD-only rule landed, so there
 * this is USD alone in practice. A staging or developer database can still
 * hold older MXN or CAD test rows: the migration leaves the rule NOT VALID on
 * such a table and warns, rather than failing the deploy. A read type that
 * shows a stored row's currency is typed with this, so an old CAD row is shown
 * in CAD rather than relabelled USD. Nothing writes with it.
 */
export const storedCurrencies = ["USD", "MXN", "CAD"] as const;
export type StoredCurrency = (typeof storedCurrencies)[number];

/**
 * The currencies a receipt's own amount may be kept in, as a reference beside
 * the USD charge on its invoice (`vendor_invoice.receipt_currency`, migration
 * `20261008194136_report_foreign_receipt_reference`, whose CHECK admits exactly
 * these). A reference, never money: nothing adds it up.
 */
export const receiptReferenceCurrencies = ["MXN", "CAD"] as const;
export type ReceiptReferenceCurrency = (typeof receiptReferenceCurrencies)[number];

/** What a currency field means when nobody chose one. */
export const DEFAULT_CURRENCY: Currency = "USD";

/**
 * Short uppercase labels for the cost-row kind tags.
 *
 * Shortened deliberately: the tag sits in a narrow column beside an amount, and
 * `WARRANTY_RECOVERY` would push the amount off the row. The full kind is always
 * available in the tag's `title`.
 */
export const costKindLabels: Record<CostKind, string> = {
  part: "PART",
  labour_internal: "LABOR",
  labour_vendor: "VENDOR",
  tax: "TAX",
  shipping: "SHIP",
  core_charge: "CORE",
  discount: "DISCOUNT",
  credit: "CREDIT",
  rebate: "REBATE",
  refund: "REFUND",
  warranty_recovery: "WARRANTY",
  adjustment: "ADJUST",
  reversal: "REVERSAL",
};

/**
 * What KIND of asset something is. **Stored, as of migration 0047**, and the
 * database agrees: `asset_asset_class_chk` admits exactly the values of
 * `assetClasses` below and nothing else (three until TRBUD-348 added
 * `company_vehicle`).
 *
 * Trucks and tractors are BOTH `truck`. The distinction people draw in
 * conversation -- tractor, cube van, sprinter -- is a vehicle-type DETAIL and
 * lives in `assetType` beside it, which stays free text because it is a
 * description rather than a category anything groups by.
 *
 * **This replaced a read-time guess.** Until 0047 the tab split was computed on
 * every request by `classifyAssetType` below, matching substrings against free
 * text -- because `asset_type` was one column holding the class, the detail, and
 * in two rows an entire forklift specification plate. The buckets `other` and
 * `unclassified` existed to catch what that guessing could not place. The class
 * is now a column, so the guess is only needed at INGEST, where the projection
 * has to classify a vendor record that has never been seen before.
 */
/**
 * `vendor_status_chk`, exactly.
 *
 * It allowed `active|inactive` until migration `0086` widened it, and several
 * comments across the codebase still said so months later — including the one on
 * the Blacklist control, which was disabled on the grounds that "there is no
 * status value that means blacklisted". There is.
 *
 * The four map one-to-one onto what the Vendors screen already displays, so the
 * badge stops being a demo overlay and becomes a read of the column.
 */
export const vendorStatuses = ["active", "conditional", "inactive", "blacklisted"] as const;
export type VendorStatus = (typeof vendorStatuses)[number];

/** The badge the Vendors screen renders, one per stored value. */
export const vendorStatusDisplay: Record<VendorStatus, VendorDisplayStatus> = {
  active: "APPROVED",
  conditional: "CONDITIONAL",
  inactive: "INACTIVE",
  blacklisted: "BLACKLISTED",
};

export type VendorDisplayStatus = "APPROVED" | "CONDITIONAL" | "INACTIVE" | "BLACKLISTED";

/** What a person picks in the status dialog, and what it means. */
export const vendorStatusLabels: Record<VendorStatus, string> = {
  active: "Approved — usable for new work",
  conditional: "Conditional — usable, with reservations",
  inactive: "Inactive — not in current use",
  blacklisted: "Blacklisted — must not be used",
};

/**
 * The storable classes, in tab order.
 *
 * **`company_vehicle` (TRBUD-348, 12 October 2026)** is a car or light vehicle
 * staff drive: the company Lincoln, not a work truck. It is NOT a power unit
 * (`readFleetSize` counts `truck` only) and it gets no PM rule and no DOT
 * inspection by default. It is placed by a person and is deliberately NOT a
 * hint in `classifyAssetType` or in the SQL classifier: "car" would also match
 * "cargo van" and "carrier", the over-matching mistake that once filed 32
 * tractors as trailers. A pickup or van that works jobs stays a `truck`.
 *
 * It is one named class rather than a "Misc" bin on purpose. The vocabulary had
 * an `other` bucket and dropped it (see the note above): a catch-all reads as a
 * fact while meaning "nobody decided", and it competes with Unclassified.
 *
 * **Add a class here only together with the four database CHECKs** (see
 * `asset_asset_class_chk`, `pm_interval_rule_asset_class_chk`,
 * `compliance_requirement_asset_class_chk` and, since TRBUD-406,
 * `spare_stock_rule_asset_class_chk`); `asset-class-parity.dbtest.test.ts`
 * fails when the two lists differ. And the database must never hold a class the
 * code does not know: the list is read from here for the Assets tab counts.
 */
export const assetClasses = ["truck", "trailer", "forklift", "company_vehicle"] as const;
export type AssetClass = (typeof assetClasses)[number];

export const assetClassLabels: Record<AssetClass, string> = {
  truck: "Trucks",
  trailer: "Trailers",
  forklift: "Forklifts",
  company_vehicle: "Company vehicles",
};

/**
 * The Assets screen's tabs. Note this is NOT `assetClasses` -- it carries one
 * extra entry that is deliberately not a storable value.
 *
 * `asset_class` is nullable: the identity projection creates assets from
 * whatever a vendor reported, which is sometimes a unit number and nothing else.
 * NULL there means "nobody has classified this yet", which is a real state a
 * user needs to see and act on, and is not a fourth kind of asset. So it is a
 * filter the screen offers, never a value the database stores.
 */
export const assetClassFilters = [...assetClasses, "unclassified"] as const;
export type AssetClassFilter = (typeof assetClassFilters)[number];

export const assetClassFilterLabels: Record<AssetClassFilter, string> = {
  ...assetClassLabels,
  unclassified: "Unclassified",
};

const TRAILER_HINTS = ["trailer", "dry van", "reefer", "flatbed", "chassis"];
const TRUCK_HINTS = [
  "vehicle",
  "truck",
  "tractor",
  "van",
  "straight",
  "kenworth",
  "freightliner",
  "peterbilt",
  "volvo",
  "sprinter",
];
const FORKLIFT_HINTS = ["forklift", "yard dog", "spotter"];

/**
 * Classify a free-text asset type at INGEST time.
 *
 * This is no longer what the Assets screen reads -- that reads the stored
 * `asset_class`. Its remaining job is the projection: an asset arriving from a
 * vendor has a type string and no class, and something has to make the first
 * guess so the row is not born unclassified.
 *
 * **Returns `null` rather than guessing when it cannot tell.** It used to fall
 * back to `other`, which under a three-value vocabulary would mean silently
 * filing an unknown machine as a forklift. A null lands the asset in the
 * Unclassified tab, where a person can settle it -- an empty that prompts
 * someone beats a wrong value that reads as a fact.
 *
 * Forklift hints are checked FIRST because a forklift specification plate
 * mentions "truck" repeatedly and would otherwise be filed as a tractor.
 * Trailer beats truck for the same reason it always did: `53 ft Plated Trailer`
 * contains neither "truck" nor "tractor", but "truck trailer" matches both and
 * in a fleet context the trailer reading is the right one.
 */
export function classifyAssetType(assetType: string | null): AssetClass | null {
  if (assetType === null || assetType.trim() === "") return null;
  const value = assetType.toLowerCase();
  if (FORKLIFT_HINTS.some((hint) => value.includes(hint))) return "forklift";
  if (TRAILER_HINTS.some((hint) => value.includes(hint))) return "trailer";
  if (TRUCK_HINTS.some((hint) => value.includes(hint))) return "truck";
  return null;
}

/**
 * `asset.ownership` CHECK -- `asset_ownership_chk` allows these two or NULL.
 *
 * Exported because the create-asset form needed them and the alternative was a
 * free-text box: the request schema accepted any string up to 40 characters
 * while the database accepted exactly two, so "Leased" would have reached the
 * user as an opaque CHECK violation from Postgres rather than as a choice they
 * never had.
 */
export const ownerships = ["company", "owner_operator"] as const;
export type Ownership = (typeof ownerships)[number];

export const ownershipLabels: Record<Ownership, string> = {
  company: "Company owned",
  owner_operator: "Owner operator",
};

/**
 * `asset.status` CHECK. **Five values** — and NOT the five display chips the UI
 * draws from downtime, which are a different set entirely (`assetDisplayStates`
 * below). The two being the same size is a coincidence, not a correspondence.
 *
 * The comment here said "Three values" while listing four, for long enough that
 * two readers had to count. `disposed` was added 27 August 2026.
 *
 * These describe OWNERSHIP AND LIFECYCLE — what we have and whether we still have
 * it. They change a handful of times in an asset's life. Operational state (idle,
 * on a load, in the shop) oscillates daily and lives in downtime events instead.
 */
export const assetStatuses = [
  "active",
  "inactive",
  "decommissioning",
  "retired",
  "disposed",
] as const;
export type AssetStatus = (typeof assetStatuses)[number];

export const assetStatusLabels: Record<AssetStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  decommissioning: "Decommissioning",
  retired: "Retired",
  disposed: "Disposed",
};

/**
 * Hover text for each status, because four of the five sound alike.
 *
 * Requested by Eden on 27 August 2026 when `disposed` was added, and the reason
 * is real: *inactive*, *decommissioning*, *retired* and *disposed* all read as
 * "not working right now" to anyone who has not been told otherwise, and picking
 * the wrong one puts an asset we still own outside the fleet, or an asset we sold
 * inside it.
 *
 * **The line that actually matters is ownership.** `retired` means we STILL OWN
 * IT; `disposed` means it is GONE. Everything above `retired` is a matter of
 * whether it is working today.
 *
 * `disposed` is the only one the database ties to another column: `asset` CHECKs
 * that a disposed asset has a `disposed_on` date and that a dated one is
 * disposed, so this status cannot be set casually or forgotten on a sale.
 */
export const assetStatusDescriptions: Record<AssetStatus, string> = {
  active: "In service and available for work.",
  inactive:
    "Temporarily out of use and expected back — parked, seasonal, or waiting on a driver. Still ours and still serviceable.",
  decommissioning:
    "On its way out of the fleet. Still owned and possibly still moving, but being wound down and not to be assigned new work.",
  retired:
    "Permanently out of service, but WE STILL OWN IT — sitting in a yard, not being repaired or assigned. Use Disposed once it leaves.",
  disposed:
    "NO LONGER OURS — sold or scrapped. Requires a disposal date, and records the proceeds. This is the only status that means the asset has left the company.",
};

/**
 * The asset status chips the Assets table renders — a DISPLAY state derived
 * from open downtime intervals, never from `asset.status` and never from stage
 * names.
 *
 * H6 is unresolved: `asset_downtime.reason` is free `text` with no CHECK, so
 * the mapping from a stored reason to one of these five is a convention this
 * package owns rather than a constraint the database enforces. See
 * `downtimeReasons` for the vocabulary the UI writes.
 */
export const assetDisplayStates = [
  "in_service",
  "down_roadside",
  "down_in_shop",
  "down_in_bay",
  "testing",
  /**
   * Out of the fleet, from `asset.status` — the one state on this list that is
   * NOT derived from a downtime interval, and it is here because its absence
   * was a false claim rather than a missing feature.
   *
   * A retired unit has no open downtime interval, because a retired unit is not
   * in a shop. So `displayStateForDowntimeReason(null)` returned `in_service`
   * and the Assets table showed a retired trailer with an **IN SERVICE** chip.
   * Lifecycle outranks downtime here: "we no longer run this" is a stronger
   * fact about availability than "nothing is being repaired on it".
   */
  "retired",
  /**
   * Parked, from `asset.status` (TRBUD-337 D5, built as the recommended
   * default): an `inactive` or `decommissioning` unit with no open downtime
   * interval used to read IN SERVICE and count as available, while the status
   * description says decommissioning is "not to be assigned new work". Each has
   * its own chip now and is never IN SERVICE. They replace IN SERVICE only: a
   * parked unit that is down in a bay still reads DOWN, because the downtime is
   * the more useful fact and it is already counted as down.
   */
  "inactive",
  "decommissioning",
] as const;
export type AssetDisplayState = (typeof assetDisplayStates)[number];

export const assetDisplayStateLabels: Record<AssetDisplayState, string> = {
  in_service: "IN SERVICE",
  down_roadside: "DOWN — ROADSIDE",
  down_in_shop: "DOWN — IN SHOP",
  down_in_bay: "DOWN — IN BAY",
  testing: "TESTING",
  retired: "OUT OF FLEET",
  inactive: "INACTIVE",
  decommissioning: "DECOMMISSIONING",
};

/** Lifecycle statuses that put a unit outside the fleet: we no longer run it. */
export const assetStatusesOutOfFleet = ["retired", "disposed"] as const;

/**
 * Lifecycle statuses that keep a unit in the fleet but not available for work
 * (TRBUD-337 D5, recommended default A): they stay in the fleet total and count
 * as not available.
 */
export const assetStatusesParked = ["inactive", "decommissioning"] as const;

/**
 * Where one unit stands for availability. THE one rule: the Assets chip, the
 * Assets "in service" filter, the Reports availability card and the Board's
 * in-service count all read it, so they cannot disagree about one unit.
 *
 * - `out_of_fleet`: retired or disposed. Outside every availability figure.
 * - `down`: an open downtime interval, any reason (testing included: the unit is
 *   not back on the road until it is released).
 * - `parked`: inactive or decommissioning with no open downtime.
 * - `available`: everything else.
 *
 * Out of fleet outranks downtime, as the chip always has; downtime outranks
 * parked, so a decommissioning unit down in a bay is counted down.
 */
export type AssetAvailability = "available" | "down" | "parked" | "out_of_fleet";

export function assetAvailability(
  status: string | null,
  downtimeReason: string | null,
): AssetAvailability {
  if ((assetStatusesOutOfFleet as readonly (string | null)[]).includes(status)) return "out_of_fleet";
  if (downtimeReason !== null) return "down";
  if ((assetStatusesParked as readonly (string | null)[]).includes(status)) return "parked";
  return "available";
}

/**
 * The chip for one asset, from its lifecycle status **and** its open downtime.
 *
 * The order is the whole point: `retired` and `disposed` win over any downtime
 * reason, including a null one. Reading downtime alone is what labelled a
 * retired trailer IN SERVICE — an asset the business no longer runs cannot be
 * available, and a chip that says otherwise is the same overstatement the
 * board's `inServiceCount` was making.
 */
export function displayStateForAsset(
  status: string | null,
  downtimeReason: string | null,
): AssetDisplayState {
  switch (assetAvailability(status, downtimeReason)) {
    case "out_of_fleet":
      return "retired";
    case "parked":
      return status === "inactive" ? "inactive" : "decommissioning";
    default:
      return displayStateForDowntimeReason(downtimeReason);
  }
}

/**
 * The controlled downtime-reason vocabulary the UI offers (H6).
 *
 * The column accepts any text, so this is the *proposed* vocabulary, applied
 * on write by the API. Until a CHECK lands, a row written by anything else can
 * still carry a reason outside this list — which is why the mapper below has a
 * fallback rather than a lookup.
 */
export const downtimeReasons = [
  "roadside_failure",
  "in_shop_repair",
  "in_bay_repair",
  "post_repair_testing",
  "planned_maintenance",
  "dot_inspection",
  "other",
] as const;
export type DowntimeReason = (typeof downtimeReasons)[number];

export const downtimeReasonLabels: Record<DowntimeReason, string> = {
  roadside_failure: "Roadside failure",
  in_shop_repair: "In shop",
  in_bay_repair: "In bay",
  post_repair_testing: "Post-repair testing",
  planned_maintenance: "Planned maintenance",
  dot_inspection: "DOT inspection",
  other: "Other",
};

/** H6's mapping, in one place. An unrecognised reason reads as generic down. */
export function displayStateForDowntimeReason(reason: string | null): AssetDisplayState {
  switch (reason) {
    case null:
      return "in_service";
    case "roadside_failure":
      return "down_roadside";
    case "in_bay_repair":
      return "down_in_bay";
    case "post_repair_testing":
      return "testing";
    case "in_shop_repair":
    case "planned_maintenance":
    case "dot_inspection":
      return "down_in_shop";
    default:
      return "down_in_shop";
  }
}

/**
 * The "Log request" dialog's Origin segmented control, written to
 * `maintenance_request.source`.
 *
 * There is **no CHECK on that column** — it is free text defaulting to
 * `'manual'`, and the design handoff's `maintenance_request.origin = phone` names
 * a column that does not exist. So this is a vocabulary the API applies on
 * write, not one the database enforces: a row landed by any other path can
 * carry a source outside this list, and readers must tolerate that.
 *
 * **This is the API-RECOGNISED list, and it is deliberately narrower than what
 * the column holds** — see `readableRequestSources`. It is narrower again on
 * the way IN: `logRequestBody` builds its `z.enum` from
 * `manuallyLoggedRequestSources`, not from this array, so membership here is
 * not by itself permission to claim a value.
 *
 * That distinction matters more than it looks: `source` is half of the
 * idempotency key `(source, source_request_id)`, so admitting `maintainx` here
 * would let a caller submit a client-chosen key in MaintainX's namespace and
 * collide with a real MaintainX request id. Keep imported provenances out.
 */
export const requestSources = ["driver_app", "phone", "walk_up", "yard_check", "automation", "manual"] as const;
export type RequestSource = (typeof requestSources)[number];

/**
 * The origins a person logging a request BY HAND may claim — the writable
 * subset of `requestSources`, and what `logRequestBody` builds its enum from.
 *
 * Two values are held back, for two different reasons, and neither is a
 * capability check: `POST /requests` and `POST /requests/log` both require
 * `createWorkOrder`, so this is about provenance being TRUE rather than about
 * who may write.
 *
 * - **`driver_app` is owned by another route.** `POST /requests` hard-codes it
 *   and never reads a source from the body. Leaving it claimable here let a
 *   phone call be recorded as a driver submission — indistinguishable in the
 *   inbox from one a driver actually made — and put the two routes in one
 *   `(source, source_request_id)` namespace they were built to partition, so a
 *   client-chosen key could land on a real driver request id. That is the same
 *   collision the note above keeps `maintainx` out for, arriving from inside
 *   the writable list rather than from outside it.
 * - **`automation` asserts that no person raised it**, and there is no
 *   automation path: nothing in the repo writes the value. A human claiming it
 *   is provenance that cannot be checked against anything.
 *
 * **`manual` stays, and it is not a third case of the same thing** — it is
 * the "decide later" path on `NewWorkOrderDialog` (TRBUD-251), a person raising
 * a request inside the app rather than relaying one somebody else reported.
 * Dropping it would break a shipped screen, which is why this list was read off
 * the callers rather than reasoned out from the names.
 *
 * When an automation path is built it adds its value back here — and takes
 * the same look at whether it should own a route that hard-codes it, the way
 * the driver path does.
 */
export const manuallyLoggedRequestSources = ["phone", "walk_up", "yard_check", "manual"] as const;
export type ManuallyLoggedRequestSource = (typeof manuallyLoggedRequestSources)[number];

/**
 * Every `maintenance_request.source` a READER must be prepared to render.
 *
 * A superset of `requestSources`: those are the origins a person may choose,
 * these add the ones only a projector can produce. `maintainx` is written by
 * `npm run project:requests` for MaintainX's `/workrequests` history, keyed on
 * the MaintainX request id — which is exactly why it must not be writable
 * through the API.
 *
 * The split exists because the column is unconstrained. Typing the read side as
 * the write side made `GET /requests` declare a union it could not honour the
 * moment anything but the API wrote a row, and the fix is to be honest about the
 * two vocabularies rather than to narrow the column or widen the form.
 */
export const readableRequestSources = [...requestSources, "maintainx"] as const;
export type ReadableRequestSource = (typeof readableRequestSources)[number];

/**
 * `maintenance_request.status` CHECK — three values.
 *
 * The design handoff's state names ("new", "triaged") are not among them. A
 * request is `open` until `set_request_status` decides it, and
 * `maintenance_request_decision_chk` ties `decided_at` / `decided_by_user_id` /
 * `decline_reason` to whichever terminal status was chosen.
 */
export const requestStatuses = ["open", "converted", "declined"] as const;
export type RequestStatus = (typeof requestStatuses)[number];

/**
 * Build gates, per the design handoff's scope table. These are not labels —
 * `discovery` means the screen has no backing tables and its rows are seeded
 * demo data, which the UI must say out loud.
 */
export const scopes = ["pilot", "committed", "discovery"] as const;
export type Scope = (typeof scopes)[number];

/** Freshness classification, matching `classifyFreshness` in packages/integrations. */
export const freshnessStates = ["active", "stale", "unknown"] as const;
export type Freshness = (typeof freshnessStates)[number];

/**
 * The `builtin:*` required-field registry from README section 9a.
 *
 * `transition_work_order` RESOLVES these — one `WHEN k.value = 'builtin:…'` arm
 * per key since migration `0037`, with `LIKE 'builtin:%' THEN false` catching an
 * unrecognised one, and `0056_exit_gate.sql` settling that they gate the move
 * OUT of a stage rather than the move in. This comment used to say the function
 * could not resolve any of them and checked `work_order.custom_fields` alone;
 * that was true before `0037` and has been wrong since. The API evaluates them
 * as well, so the completion panel can say what is missing before the transition
 * is attempted — but the server function is the authority, not a stand-in for
 * one that does not exist.
 */
export const builtinRequiredFields = [
  "builtin:location_at_open",
  "builtin:vendor",
  "builtin:assignee",
  "builtin:photo",
  "builtin:due_date",
  "builtin:root_cause",
  "builtin:fault_type",
  "builtin:meter_reading",
  "builtin:time_logged",
  "builtin:parts_issued",
  "builtin:final_invoice",
  // Added 20 August 2026 with the exit gate. The paperwork half of requirement
  // 1.5, and a STRICTER predicate than builtin:photo: it wants a verified
  // document, not merely a scanned one. A malware scan says the bytes are safe;
  // only a person can say this is the right receipt for this job.
  "builtin:receipt",
  // TRBUD-300 (Eden, 24 September 2026). ONE requirement with several ways to
  // meet it, for in-house work: a part issued from inventory (the stock ledger,
  // net of returns), a live part cost line (exactly `builtin:parts_issued`'s
  // predicate — Eden, 25 September 2026, on a part cost typed in through Add
  // cost: "sure"), a verified receipt (exactly `builtin:receipt`'s predicate),
  // or a recorded "No parts used" (`work_order.no_parts_used_at`). The evidence
  // may be present together. It replaced `builtin:parts_issued` plus
  // `builtin:receipt` on the seeded in-house Repair complete stage, where
  // requiring both left a stock-parts job and a labour-only job unclosable.
  "builtin:parts_accounted_for",
] as const;
export type BuiltinRequiredField = (typeof builtinRequiredFields)[number];

export const builtinRequiredFieldLabels: Record<BuiltinRequiredField, string> = {
  "builtin:location_at_open": "Location at open",
  "builtin:vendor": "Vendor",
  "builtin:assignee": "Assignee",
  "builtin:photo": "Photo",
  "builtin:due_date": "Due date",
  "builtin:root_cause": "Root cause",
  "builtin:fault_type": "Fault type",
  "builtin:meter_reading": "Meter reading",
  "builtin:time_logged": "Time logged",
  "builtin:parts_issued": "Parts issued",
  "builtin:final_invoice": "Final invoice",
  "builtin:receipt": "Receipt or invoice, verified",
  // The label names every way on purpose: it is what a refused move's
  // `missingRequiredFields` carries and what the completion panel prints.
  "builtin:parts_accounted_for":
    "Part issued from inventory, part cost, verified receipt, or No parts used",
};

/** `custom_field_def.data_type` CHECK — exactly three. No text fields, ever. */
export const customFieldDataTypes = ["date", "currency", "boolean"] as const;
export type CustomFieldDataType = (typeof customFieldDataTypes)[number];

export function isBuiltinRequiredField(key: string): key is BuiltinRequiredField {
  return (builtinRequiredFields as readonly string[]).includes(key);
}

/* ── Documents ──────────────────────────────────────────────────────── */

/**
 * `document_class_chk`. What a file IS, independent of what it is attached to.
 *
 * `unknown` is the default and means nobody has classified it, which is a
 * different fact from `other` — that one asserts a classification was made and
 * came back "none of the above". An upload with no stated class lands `unknown`.
 *
 * `dot_inspection` (18 September 2026, migration 0258) is the DOT inspection
 * report itself — the paper the inspector hands back. Eden: *"we need to add a
 * document type called 'DOT Inspection Results'"*. It is NOT financial
 * (`isFinancial` in `routes/document-rules.ts` does not list it) and NOT
 * loggable, so it never reaches the review queue; it is filed against the work
 * order or the asset and read back from the Documents card.
 */
export const docClasses = [
  "invoice",
  "receipt",
  "estimate",
  "work_order",
  "photo",
  "dot_inspection",
  "other",
  "unknown",
] as const;
export type DocClass = (typeof docClasses)[number];

export const docClassLabels: Record<DocClass, string> = {
  invoice: "Invoice",
  receipt: "Receipt",
  estimate: "Estimate",
  work_order: "Work order",
  photo: "Photo",
  dot_inspection: "DOT Inspection Results",
  other: "Other",
  unknown: "Unclassified",
};

/**
 * Which classes can be LOGGED — itemised into a vendor invoice (TRBUD-162).
 *
 * ## Narrower than `isFinancial`, on purpose
 *
 * `isFinancial` (`apps/api/src/routes/document-rules.ts`) answers "does this
 * carry money, and therefore need `viewFinancialDocuments`", and its set
 * includes **`estimate`**. This answers a different question — "is there a bill
 * here to record against the ledger" — and an estimate is a quote, not a bill.
 * Logging one would create a `vendor_invoice` for money nobody has been charged,
 * and `vendor_invoice_vendor_number_uq` on `(vendor_id, invoice_number)` would
 * then refuse the real invoice when it arrived.
 *
 * It matches the `builtin:receipt` gate's own list instead
 * (`doc_class IN ('receipt', 'invoice')`, migration 0130), which is the right
 * neighbour: "may this paperwork close the job" and "may this paperwork be
 * itemised" are two questions that should not disagree.
 *
 * Deliberately a SEPARATE list rather than an expression over `isFinancial`'s.
 * Sharing one would mean widening who may SEE financial documents silently also
 * widened what may be POSTED to the ledger — the same "one key per DECISION"
 * reasoning `routine_permission` is built on.
 */
export const loggableDocClasses = ["invoice", "receipt"] as const;
export type LoggableDocClass = (typeof loggableDocClasses)[number];

/** Whether this class can be itemised into a vendor invoice. */
export function isLoggableDocClass(docClass: DocClass): docClass is LoggableDocClass {
  return (loggableDocClasses as readonly string[]).includes(docClass);
}

/**
 * What KIND of thing an invoice line is, in the reviewer's language.
 *
 * A deliberately short list that maps onto `cost_entry_kind_chk`'s much longer
 * one, and the mapping is the point: a person reading a vendor invoice sees
 * parts, labour, fees, tax and discounts, not eleven accounting kinds. The
 * server owns the translation (`COST_KIND_BY_LINE` in `routes/intake.ts`).
 *
 * `LABOR` maps to `labour_vendor`, **never** `labour_internal`:
 * `cost_entry_internal_labour_chk` requires an internal labour row to carry a
 * `time_entry_id` and a `labour_rate_id`, and `post_manual_cost` refuses the
 * kind outright. A vendor's labour line is not our mechanic's time.
 *
 * `DISCOUNT` is the only one that posts NEGATIVE — `cost_entry_sign_chk` puts
 * every recovery kind on the negative side and derives `accounting_category`
 * from the sign — which is why the manual entry form takes a positive magnitude
 * and the server applies the sign from the kind. Asking a person to type a minus
 * is how a credit gets posted as a charge.
 *
 * Screaming case because these are the labels the review queue has shown since
 * the demo inbox landed, and `InvoiceLine.kind` is already this union on the
 * wire. Named here so zod can validate against it instead of a hand-copied
 * enum — a second copy is a second place for the vocabulary to drift.
 */
export const invoiceLineKinds = ["PART", "LABOR", "FEE", "DISCOUNT", "TAX"] as const;
export type InvoiceLineKind = (typeof invoiceLineKinds)[number];

export const invoiceLineKindLabels: Record<InvoiceLineKind, string> = {
  PART: "Part",
  LABOR: "Vendor labour",
  FEE: "Fee or shipping",
  DISCOUNT: "Discount or credit",
  TAX: "Tax",
};

/**
 * `work_order_document_relationship_chk`. What a file is DOING on this work order.
 *
 * Separate from `doc_class` because the same document can mean different things
 * on different parents, and `receipt` was added in migration 0054 — the class had
 * it from the start and the relationship did not, which left the one intake
 * source Eden called mandatory as the one the vocabulary could not name.
 */
export const documentRelationships = [
  "attachment",
  "invoice",
  "estimate",
  "photo",
  "signature",
  "receipt",
] as const;
export type DocumentRelationship = (typeof documentRelationships)[number];

export const documentRelationshipLabels: Record<DocumentRelationship, string> = {
  attachment: "Attachment",
  invoice: "Invoice",
  estimate: "Estimate",
  photo: "Photo",
  signature: "Signature",
  receipt: "Receipt",
};

/** `asset_document_relationship_chk`. Papers, not photos — see `asset_photo`. */
export const assetDocumentRelationships = [
  "title",
  "registration",
  "insurance",
  "inspection",
  "warranty",
  "purchase",
  "attachment",
] as const;
export type AssetDocumentRelationship = (typeof assetDocumentRelationships)[number];

export const assetDocumentRelationshipLabels: Record<AssetDocumentRelationship, string> = {
  title: "Title",
  registration: "Registration",
  insurance: "Insurance",
  inspection: "Inspection",
  warranty: "Warranty",
  purchase: "Bill of sale",
  attachment: "Attachment",
};

/**
 * `document_scan_status_chk`. The malware verdict, and only a scanner writes it.
 *
 * `failed` and `quarantined` are NOT synonyms and the difference decides whether
 * a retry is legal: `failed` means the scan could not RUN (engine error, timeout,
 * unreadable), `quarantined` means it FOUND something. So a `failed` document can
 * still become `passed`; a quarantined one cannot, without a human override that
 * does not exist yet.
 */
export const scanStatuses = ["pending", "passed", "failed", "quarantined"] as const;
export type ScanStatus = (typeof scanStatuses)[number];

export const scanStatusLabels: Record<ScanStatus, string> = {
  pending: "Awaiting security scan",
  passed: "Scanned",
  failed: "Scan could not run",
  quarantined: "Quarantined",
};

/**
 * What may be previewed in the browser, and when.
 *
 * Images are permitted while pending because that is the common case — a phone
 * photo of a till slip — and blocking them would make the verification surface
 * unusable before Defender exists.
 *
 * SVG is excluded from both lists despite being an image: an inline
 * `image/svg+xml` executes script in the app's own origin.
 *
 * ## Y14 is SUPERSEDED — read `previewBasisFor` below
 *
 * Y14 (Eden, 19 August 2026) blocked **pending PDF, HEIC and HEIF until the scan
 * passes**, and rejected the obvious workaround: *"`nosniff` and offering a
 * download instead prevent inline RENDERING; they do not stop a person opening
 * the file, so 'download only' is not a safe alternative for anything
 * unscanned."* Both halves were right **on their premise, which was that Defender
 * was coming.**
 *
 * That premise is gone (Eden, 3 September 2026: *"if SB has no malware then skip
 * it for now"*), and Brett supplied the model that replaces it: the **intake
 * channel** carries the defence. Mail is scanned by Microsoft before it reaches
 * anyone; in-person upload is one named, trusted human. SigmaBuddy runs on
 * exactly this reasoning, which is why it has no malware scanning at all.
 *
 * So the lists below are unchanged and Y14's *conclusion* is replaced: the
 * question is no longer only "has it been scanned" but "has it been scanned **or
 * did it arrive by a channel that is its own defence**". `previewBasisFor` answers
 * that and NAMES which of the two, so every allowance is countable rather than
 * assumed. Note the download-only route stays rejected — it was the weaker idea
 * and nothing here revives it. Its one exception, a PDF the MaintainX import
 * landed, was served as a download from 28 September to 2 October 2026 and now
 * opens in the page instead (Eden's ruling of 2 October): the `mainx_archive`
 * basis of `deliveryBasisFor`, recorded there.
 */
export const previewableWhilePending = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export const previewableOncePassed = [
  ...previewableWhilePending,
  "application/pdf",
  "image/heic",
  "image/heif",
] as const;

/**
 * Why a document may be previewed. `null` from `previewBasisFor` means it may not.
 *
 * A closed vocabulary rather than a boolean, for the reason CLAUDE.md's cold-start
 * rule gives about `basis` columns generally: it is the only way to ask afterwards
 * **how much was waved through, and on what grounds.** With a bare boolean, a file
 * shown because Microsoft scanned it upstream is indistinguishable from one shown
 * because a scan actually passed.
 */
export const previewBases = [
  /** A real verdict from a real scanner. The only basis that is evidence. */
  "defender_passed",
  /** Arrived through a mailbox Microsoft 365 had already scanned. */
  "email_scanned_upstream",
  /** Uploaded in person by a named, trusted user — Brett's "inherent defense". */
  "trusted_uploader",
  /** A plain raster image awaiting a scan. Predates the channel model and is unchanged by it. */
  "image_while_pending",
] as const;
export type PreviewBasis = (typeof previewBases)[number];

/**
 * Intake channels whose own path is the defence, and the basis each supplies.
 *
 * `api` and `migration` are deliberately ABSENT, and that absence is the design.
 * An API feed has no human in the path and no Microsoft scanner upstream, and it
 * **does not exist yet** — so refusing it now costs nothing, where letting it
 * inherit the waiver would open a gate before anything had ever tested it. That is
 * the cold-start mistake in the opposite direction. `scanner` (a physical document
 * scanner, not a malware one) is absent for the same reason: nobody has built it,
 * so nobody has decided.
 */
const previewDefendedChannels: Readonly<Record<string, PreviewBasis>> = {
  email: "email_scanned_upstream",
  upload: "trusted_uploader",
};

/**
 * The basis on which this document may be previewed, or `null` to refuse.
 *
 * `intakeChannel` is REQUIRED rather than optional on purpose: an optional
 * parameter lets a caller forget it and silently get the old, stricter answer,
 * which is a bug that looks like a policy. Making it required means the compiler
 * finds every call site.
 */
export function previewBasisFor(
  mimeType: string,
  scanStatus: ScanStatus,
  intakeChannel: string | null,
): PreviewBasis | null {
  // `failed` is refused alongside `quarantined` even though it means "the scan
  // could not run" rather than "malware": an unscannable file is one nobody has
  // cleared, and no intake channel overrides a verdict that came back bad. This
  // is checked FIRST so a defended channel can never launder a real finding.
  if (scanStatus === "quarantined" || scanStatus === "failed") return null;

  const mime = mimeType.toLowerCase();
  if (!(previewableOncePassed as readonly string[]).includes(mime)) return null;
  if (scanStatus === "passed") return "defender_passed";

  // Pending. The pre-existing image allowance is kept exactly as it was, so
  // nothing that renders today stops rendering: this function only ever ADDS.
  if ((previewableWhilePending as readonly string[]).includes(mime)) {
    return "image_while_pending";
  }
  // Not `intakeChannel && …`: for an empty string that expression evaluates to
  // `""`, which is neither a basis nor null and does not satisfy the return type.
  if (!intakeChannel) return null;
  return previewDefendedChannels[intakeChannel] ?? null;
}

export function canPreview(
  mimeType: string,
  scanStatus: ScanStatus,
  intakeChannel: string | null,
): boolean {
  return previewBasisFor(mimeType, scanStatus, intakeChannel) !== null;
}

/**
 * Could this type EVER be previewed, scan and channel aside?
 *
 * Split out because `previewRefusal` asks the type question first — it is the one
 * that cannot be fixed by waiting — and it previously did so by calling
 * `canPreview(mime, "passed")`, which now needs a channel it has no business
 * knowing.
 */
export function isPreviewableType(mimeType: string): boolean {
  return (previewableOncePassed as readonly string[]).includes(mimeType.toLowerCase());
}

/**
 * Why a SCREEN may show a document in the page: every `previewBasisFor` answer,
 * and one more. `null` from `deliveryBasisFor` means the content route refuses
 * the file outright.
 *
 * ## The screens' rule is one allowance wider than `canPreview`
 *
 * `mainx_archive` is a pending PDF the MaintainX import landed: intake channel
 * `migration`, with a `mainx_api` row in `document_source_ref`. Eden ruled on
 * 2 October 2026 that it opens in the page like an uploaded PDF and is never
 * handed over as a download: *"I dont want to download the receipts odds are the
 * user reviewing it is the one that uploaded it so they already have the file
 * and now you've clogged up their computer for no reason with two copies of the
 * same receipt"*. His reasoning on trust: CES staff uploaded these files to
 * MaintainX, and TruckBuddy pulled them over MaintainX's API, so they are no less
 * trusted than a file the same person uploads to TruckBuddy directly.
 *
 * It is a basis of its own rather than a channel in `previewDefendedChannels`,
 * for two reasons. Every file shown on it stays countable. And `migration` itself
 * stays undefended: a later migration writer (requirement 11.46, the local scan
 * archive) has no MaintainX source row, so it is still refused until its own
 * defence is decided.
 *
 * `canPreview` does NOT include it, on purpose. The receipt parser and the
 * training export read only what `canPreview` admits, and the ruling was about
 * what a person sees, so neither reads a MaintainX PDF. `tb_document_cost_basis`
 * (migration 0159) still refuses one as cost evidence.
 */
export const deliveryBases = [...previewBases, "mainx_archive"] as const;
export type DeliveryBasis = (typeof deliveryBases)[number];

/**
 * The basis on which a screen may show this document in the page, or `null`
 * when the content route refuses it. See `deliveryBases`.
 *
 * `importedFromMainx` means the document has a `document_source_ref` row with
 * `source_system = 'mainx_api'` (`importedFromMainxSql` in the API). It is
 * REQUIRED for the reason `previewBasisFor` gives about `intakeChannel`: a caller
 * that could leave it out would silently get the stricter answer, and the
 * compiler finds every call site.
 */
export function deliveryBasisFor(
  mimeType: string,
  scanStatus: ScanStatus,
  intakeChannel: string | null,
  importedFromMainx: boolean,
): DeliveryBasis | null {
  const basis = previewBasisFor(mimeType, scanStatus, intakeChannel);
  if (basis !== null) return basis;
  // Exactly what the ruling covers and nothing wider. A quarantined or failed
  // file is refused because only `pending` qualifies; the flag buys nothing on
  // the `api` or `scanner` channel, nor without the flag on `migration`; and no
  // type but PDF qualifies.
  if (
    importedFromMainx &&
    intakeChannel === "migration" &&
    scanStatus === "pending" &&
    mimeType.toLowerCase() === "application/pdf"
  ) {
    return "mainx_archive";
  }
  return null;
}

/**
 * How `GET /documents/:id/content` hands a document over: in the page. The
 * other answer is a refusal, which is `null` rather than a value of this type.
 */
export type DocumentDelivery = "inline";

/**
 * How the content route hands this document over: `inline` (shown in the page)
 * or `null` (not at all: a 403).
 *
 * ## One rule for every reader (TRBUD-323)
 *
 * The content route, the Review reader and both document lists ask this
 * function, on the same four inputs, so a screen never offers a preview the
 * route will not give.
 *
 * ## Nothing is a download
 *
 * This used to answer `attachment` for one case, the pending MaintainX PDF,
 * which the route served as a download from 28 September 2026. Eden's ruling of
 * 2 October made that case `inline` (the `mainx_archive` basis), so the answer
 * is only ever `inline` or `null`. A file the route refuses is not offered as a
 * download either: whatever refuses it in the page refuses it as a file too.
 */
export function documentDelivery(
  mimeType: string,
  scanStatus: ScanStatus,
  intakeChannel: string | null,
  importedFromMainx: boolean,
): DocumentDelivery | null {
  return deliveryBasisFor(mimeType, scanStatus, intakeChannel, importedFromMainx) === null
    ? null
    : "inline";
}

/**
 * What may be UPLOADED at all — an allowlist, not a shape (F-26, TRBUD-120).
 *
 * `document_mime_type_chk` and `normaliseMime` check the SHAPE of the string and
 * nothing else, so `application/x-msdownload` satisfied both and a `tool.exe`
 * declared as one was stored, in the blob and in `document`. The serve path does
 * defend the download — `canPreview` refuses it in every scan state, so it 403s —
 * which is why this is a storage and honesty defect rather than an exploit: an
 * arbitrary binary sat in the store, and the only refusal a person ever saw was
 * a 403 long after the upload said 200.
 *
 * ## It is a SUPERSET of `previewableOncePassed`, deliberately
 *
 * Anything the viewer would render has to be uploadable, or two rules disagree
 * about the same file. The extra entries are the paperwork a shop actually sends
 * and TruckBuddy stores rather than renders. (This said they "download as
 * attachments"; they never did. The content route refuses them, which
 * `documentDelivery` now states in one place.)
 *
 * ## What is deliberately absent
 *
 * `image/svg+xml` — the one image type `canPreview` refuses in EVERY scan state,
 * so accepting it stores a file whose only possible future is a 403.
 * `application/octet-stream` — "I did not look" is not a type, and a stated type
 * that is wrong is worse than an absent one because the serve route hands it
 * straight to the browser. Archives and executables are absent for the obvious
 * reason: nothing in TruckBuddy's workflow attaches one to a work order.
 */
export const uploadableMimeTypes = [
  ...previewableOncePassed,
  // A multifunction scanner in a shop office produces these two beside PDF.
  "image/tiff",
  "image/bmp",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;

/** Whether the upload route will accept this type at all. Case-insensitive. */
export function isUploadableMime(mimeType: string): boolean {
  const allowed: readonly string[] = uploadableMimeTypes;
  return allowed.includes(mimeType.toLowerCase());
}

/* ── do the BYTES agree with the declared type? (TRBUD-155) ──────── */

/**
 * Why the allowlist above is not enough, and what this adds.
 *
 * `isUploadableMime` reads the content type the CLIENT DECLARES and never looks
 * at the file. Measured 3 September 2026: a 24-byte file beginning with the DOS
 * `MZ` header, uploaded as `application/pdf`, answered **200** and was stored in
 * blob storage and in `document`. So the allowlist stops an honest mistake and a
 * naive client; it does not stop anyone willing to set a header, which is one
 * curl flag.
 *
 * That matters more than it looks, because there is nothing behind it.
 * **Defender for Storage is provisioned nowhere and is now deliberately
 * deferred** (Eden, 3 September 2026: *"if SB has no malware then skip it for
 * now"*), so `scan_status` is `pending` on every environment and no malware
 * verdict will ever arrive. Until that changes, the declared type is the only
 * thing between an arbitrary binary and the blob store — so the declared type
 * has to be TRUE.
 *
 * ## Refuse a disagreement; never reclassify
 *
 * A file whose header and label disagree is not a file anyone meant to send, and
 * silently rewriting its type would LAUNDER it: the row would then assert a fact
 * about the bytes that nobody checked, and every later reader — the viewer, the
 * receipt gate, a future scanner's report — would inherit it. Refusing hands the
 * problem back to the one party who can actually fix it.
 *
 * ## This is an ADDITIONAL gate, not a replacement
 *
 * The allowlist still runs first and still owns the question "would TruckBuddy
 * store this type at all". This one only ever answers "are these the bytes of the
 * type you said", which is a different question with a different refusal.
 */
export const signatureVerdicts = [
  /** The bytes carry this type's signature. */
  "verified",
  /** The type has no stable signature, so the declared type stands. */
  "unsigned",
  /** The bytes carry a DIFFERENT format's signature, or none at all. */
  "mismatch",
  /** Fewer bytes than the signature needs, so agreement cannot be shown. */
  "too_short",
] as const;
export type SignatureVerdict = (typeof signatureVerdicts)[number];

/** One run of bytes at a fixed offset. Every part of a signature must match. */
interface SignaturePart {
  readonly offset: number;
  readonly bytes: readonly number[];
}

/**
 * A format's magic bytes.
 *
 * `signatures` is a list of ALTERNATIVES — any one matching is agreement — and
 * each alternative is a list of PARTS, all of which must match. Both levels are
 * used: GIF has two version strings (`GIF87a`, `GIF89a`) and WebP has two runs at
 * two offsets (`RIFF` at 0 and `WEBP` at 8), which a single flat prefix could not
 * express without accepting any RIFF container as an image.
 */
interface MimeSignatureSpec {
  /** What to call the format in a refusal a mechanic reads. */
  readonly label: string;
  readonly signatures: readonly (readonly SignaturePart[])[];
}

const PDF: readonly SignaturePart[] = [{ offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] }]; // %PDF
const ZIP: readonly SignaturePart[] = [{ offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] }]; // PK\x03\x04
// An ISO base media file: the `ftyp` box type, four bytes into the box. The brand
// at offset 8 is deliberately NOT checked — see `mimeSignatures` below.
const FTYP: readonly SignaturePart[] = [{ offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] }];

/**
 * The signature per uploadable type, and what is deliberately missing.
 *
 * ## The four types with NO entry, each for its own reason
 *
 * `text/plain` and `text/csv` have no signature at all, and inventing a heuristic
 * for them ("is it mostly printable?") would refuse a real file for a guess — a
 * UTF-16 export, a log with a stray control byte. They pass on the declared type
 * exactly as they did before this gate existed.
 *
 * `application/msword` and `application/vnd.ms-excel` DO have one — the OLE2
 * compound-file header — and are still left out on purpose. A `.doc` or `.xls` in
 * the wild is very often not OLE2: an old client sends RTF, plain text, or an
 * OOXML file under the legacy type. Enforcing OLE2 would refuse real paperwork
 * from a real shop, which is a worse outcome than the narrow hole it closes.
 *
 * So the gate is honest rather than total: for the ten types below the declared
 * type is now PROVEN, and for those four it remains a claim. That residue is
 * recorded here rather than hidden, and `documents.test.ts` asserts the partition
 * is TOTAL — a type added to `uploadableMimeTypes` and to neither list fails the
 * suite, so the decision cannot be skipped by accident.
 *
 * ## Why the HEIC brand is not checked
 *
 * `image/heic` is verified by the presence of the `ftyp` box and nothing more. The
 * brand at offset 8 would narrow it further (`heic`, `mif1`, …), and refusing a
 * brand we simply failed to enumerate is refusing on ABSENCE of knowledge — the
 * exact move CLAUDE.md's cold-start rule forbids. The cost of the looser check is
 * that an MP4 declared `image/heic` would pass, which stores a container nothing
 * renders; the cost of the tighter one is a real iPhone photo refused for a reason
 * no error could explain.
 */
export const mimeSignatures: Readonly<Record<string, MimeSignatureSpec>> = {
  "application/pdf": { label: "PDF", signatures: [PDF] },
  "image/png": {
    label: "PNG image",
    signatures: [[{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }]],
  },
  // Three bytes, not the fourth: the fourth is the marker of the first segment and
  // varies legitimately (0xE0 JFIF, 0xE1 Exif, 0xDB, …).
  "image/jpeg": { label: "JPEG photo", signatures: [[{ offset: 0, bytes: [0xff, 0xd8, 0xff] }]] },
  "image/gif": {
    label: "GIF image",
    signatures: [
      [{ offset: 0, bytes: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61] }], // GIF87a
      [{ offset: 0, bytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61] }], // GIF89a
    ],
  },
  "image/webp": {
    label: "WebP image",
    signatures: [
      [
        { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] }, // RIFF
        { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] }, // WEBP
      ],
    ],
  },
  "image/heic": { label: "HEIC photo", signatures: [FTYP] },
  "image/heif": { label: "HEIF photo", signatures: [FTYP] },
  "image/tiff": {
    label: "TIFF image",
    signatures: [
      [{ offset: 0, bytes: [0x49, 0x49, 0x2a, 0x00] }], // little-endian
      [{ offset: 0, bytes: [0x4d, 0x4d, 0x00, 0x2a] }], // big-endian
    ],
  },
  "image/bmp": { label: "BMP image", signatures: [[{ offset: 0, bytes: [0x42, 0x4d] }]] },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    label: "Word document",
    signatures: [ZIP],
  },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    label: "Excel workbook",
    signatures: [ZIP],
  },
};

/**
 * Uploadable types whose bytes cannot be checked, listed so the gap is countable.
 *
 * Kept as a named list rather than as "whatever is not in `mimeSignatures`",
 * because those two are not the same thing: the second silently absorbs a type
 * somebody forgot to decide about, which is how a gate stops covering the format
 * it was built for.
 */
export const unsignedUploadableMimeTypes = [
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.ms-excel",
] as const;

/**
 * How many leading bytes a caller must read for `declaredTypeVerdict` to decide.
 *
 * Computed from the table rather than written down, so adding a signature at a
 * deeper offset cannot leave the reader peeking too few bytes — a mistake that
 * would surface as `too_short` on perfectly good files.
 */
export const signatureHeadBytes: number = Object.values(mimeSignatures)
  .flatMap((spec) => spec.signatures)
  .flat()
  .reduce((most, part) => Math.max(most, part.offset + part.bytes.length), 0);

/** What to call this format in a refusal, or `null` if nothing checks it. */
export function signatureFormatLabel(mimeType: string): string | null {
  return mimeSignatures[mimeType.toLowerCase()]?.label ?? null;
}

function partMatches(head: Uint8Array, part: SignaturePart): "yes" | "no" | "undecided" {
  for (let i = 0; i < part.bytes.length; i += 1) {
    const at = part.offset + i;
    // Every byte so far agreed and the file stopped. Nothing here is wrong yet,
    // and nothing is proven either — which is a third answer, not a `no`.
    if (at >= head.length) return "undecided";
    if (head[at] !== part.bytes[i]) return "no";
  }
  return "yes";
}

/**
 * Do these leading bytes agree with the declared type?
 *
 * A closed vocabulary rather than a boolean, for the reason `previewBasisFor`
 * gives one line further up: with a bare `false` a refusal cannot say whether the
 * file was the wrong format or merely truncated, and those two want different
 * sentences and different advice. `unsigned` is likewise not `true` — it records
 * that nothing was checked, so a later reader cannot mistake "passed" for
 * "verified".
 *
 * Pure, and typed on `Uint8Array` rather than `Buffer` on purpose: this package
 * ships to the browser bundle, where `Buffer` does not exist. A `Buffer` IS a
 * `Uint8Array`, so a Node caller passes one straight in.
 */
export function declaredTypeVerdict(mimeType: string, head: Uint8Array): SignatureVerdict {
  const spec = mimeSignatures[mimeType.toLowerCase()];
  if (!spec) return "unsigned";

  let undecided = false;
  for (const signature of spec.signatures) {
    const results = signature.map((part) => partMatches(head, part));
    if (results.every((r) => r === "yes")) return "verified";
    // Only "we ran out of file" counts as undecided. One part that positively
    // disagrees rules this alternative out however much is missing elsewhere.
    if (!results.includes("no") && results.includes("undecided")) undecided = true;
  }
  return undecided ? "too_short" : "mismatch";
}

/**
 * Whether a file belongs in `asset_photo` rather than `asset_document` (F-48).
 *
 * BOTH halves are load-bearing and neither alone is right. The asset upload route
 * used to split on `startsWith("image/")`, which admits `image/svg+xml` — so an
 * SVG landed in the unit's photo strip, and every render of that tile called the
 * content route and got a 403 for ever. Testing only `previewableOncePassed`
 * would be the opposite error: it contains `application/pdf`, which is paper.
 *
 * So: an image, AND one the viewer could actually render. A type that can never
 * be shown is filed as a document, where nothing tries to draw it.
 */
export function isPhotoMime(mimeType: string): boolean {
  const mime = mimeType.toLowerCase();
  const renderable: readonly string[] = previewableOncePassed;
  return mime.startsWith("image/") && renderable.includes(mime);
}

/* ── routine permissions (F48) ───────────────────────────────────── */

/**
 * The business names for the six `user_role` values.
 *
 * `permission-model.md` section 7 maps them: `manager` is the Fleet Manager,
 * `processor` is the Asset Repair Coordinator, `viewer` is Dispatch and
 * `requester` is a Driver. Those are the words the people in the building use,
 * and a switch board that says "processor" makes the reader translate before
 * they can answer "may Matt do this?".
 *
 * **The stored value never changes.** `user_role_role_chk` and
 * `routine_permission_roles_chk` both allow exactly the six codes; this is a
 * display layer over them, in the same shape as `priorityLabels`.
 *
 * Terminal Manager — Tony — is deliberately absent. It is a seventh role the
 * plan adds (3.5) and the database has no value for it yet, so naming it here
 * would put a role on screen that nothing can hold.
 */
export const userRoleLabels: Record<UserRole, string> = {
  admin: "Admin",
  manager: "Fleet Manager",
  processor: "Asset Repair Coordinator",
  mechanic: "Mechanic",
  viewer: "Dispatch",
  requester: "Driver",
};

/**
 * The areas the routine-permission switches are grouped under.
 *
 * **Grouping is code, not data, and that is deliberate.** `routine_permission`
 * has no area column and adding one would be a migration for a presentation
 * concern. The keys themselves ARE data — migrations 0054, 0058 and 0059 each
 * dropped a new one in — so this is a hint, never a filter: `routineAreaFor`
 * sends an unrecognised key to `other` rather than hiding it. A switch that
 * exists in the database and not on the screen is precisely the failure F48
 * exists to end.
 */
export const routineAreas = [
  {
    id: "money",
    title: "Money",
    blurb: "Who may attach a cost to a repair, reverse one, and pay for it.",
  },
  { id: "parts", title: "Parts and stock", blurb: "Who may move stock on and off the shelf." },
  {
    id: "requests",
    title: "Requests",
    blurb: "Who may convert or decline a maintenance request, and reopen one whose work order was cancelled.",
  },
  {
    id: "assets",
    title: "Assets",
    blurb: "Who may end a unit's life, accept a proposed merge, and record or void a DOT inspection.",
  },
  {
    id: "documents",
    title: "Documents",
    blurb: "Who may attest a document is the right one, and how long it is kept.",
  },
  {
    id: "system",
    title: "System",
    blurb: "Who may take TruckBuddy down for maintenance.",
  },
  { id: "other", title: "Other", blurb: "Switches this build has no area for yet." },
] as const;

export type RoutineArea = (typeof routineAreas)[number]["id"];

/** The keys seeded by migrations 0050, 0054, 0058 and 0059, by area. */
const ROUTINE_AREA_BY_KEY: Record<string, RoutineArea> = {
  post_cost: "money",
  post_time: "money",
  // TRBUD-183. Both voids are money decisions even though one of them is about
  // hours: voiding a time entry drops its labour cost too, which is the point of
  // the pairing. Without these two the Settings screen would file them under
  // "Other" — the fallback `routineAreaFor` gives an unknown key, which is where
  // `correct_time_entry` still sits (see the plan doc's "Found, not fixed").
  void_time_entry: "money",
  void_cost_entry: "money",
  correct_time_entry: "money",
  // TRBUD-458: moving a posted cost to the day the work actually happened.
  correct_cost_date: "money",
  // TRBUD-459: the PM bonus rate and who earned each PM's bonus.
  set_pm_bonus_rate: "money",
  correct_pm_credit: "money",
  confirm_own_pm_credit: "money",
  // TRBUD-403: Start work / Stop work. A work mark decides labour minutes under
  // the "clocked in and working" clock, so it sits beside post_time.
  work_marks: "money",
  record_payment: "money",
  manage_vendor_invoice: "money",
  move_stock: "parts",
  // TRBUD-416: a mechanic applying parts a manager handed him.
  apply_issued_parts: "parts",
  // TRBUD-442: deciding a request (convert or decline), and the reopen that a
  // cancelled work order performs (migration 0149), which sat under "Other".
  decide_maintenance_request: "requests",
  retract_request_conversion: "requests",
  manage_asset_lifecycle: "assets",
  review_asset_match: "assets",
  // TRBUD-342: recording a DOT inspection and voiding one recorded by mistake.
  record_inspection: "assets",
  void_inspection: "assets",
  verify_document: "documents",
  manage_retention_policy: "documents",
  // Receipt review modes: the AI reading switch and the parser version.
  set_receipt_reading: "documents",
  set_receipt_parser: "documents",
  // TRBUD-352: marking a file as a later page of a receipt, and joining it.
  join_receipt_pages: "documents",
  // Maintenance mode: arming, starting and ending a window.
  manage_site_maintenance: "system",
};

/** The area a switch belongs to. An unknown key lands in `other`, never nowhere. */
export function routineAreaFor(key: string): RoutineArea {
  return ROUTINE_AREA_BY_KEY[key] ?? "other";
}

/**
 * A role list in one canonical order, deduplicated and with unknown values
 * dropped.
 *
 * `allowed_roles` is a `text[]` with no ordering guarantee, so comparing two
 * lists by their string form would report a change that is only a reordering —
 * and draft-then-save would then offer to save it. Ordering by `userRoles`
 * makes the comparison meaningful and the audit delta stable.
 */
export function normaliseRoles(roles: readonly string[]): UserRole[] {
  return userRoles.filter((role) => roles.includes(role));
}

/** Whether two role lists mean the same thing, whatever order they arrive in. */
export function sameRoles(a: readonly string[], b: readonly string[]): boolean {
  const left = normaliseRoles(a);
  const right = normaliseRoles(b);
  return left.length === right.length && left.every((role, index) => role === right[index]);
}

/** One switch as the draft sees it: what it is now, and what it would become. */
export interface RoutinePermissionEdit {
  key: string;
  /** The switch's human label, so a refusal can name it rather than its key. */
  label: string;
  before: UserRole[];
  after: UserRole[];
}

/**
 * The diff between the saved switches and the draft — the only thing a save
 * sends.
 *
 * A no-op is dropped rather than written. `set_routine_permission` stamps
 * `updated_at` and `updated_by_user_id` unconditionally and the call is audited,
 * so submitting an untouched switch would forge a record of a change that did
 * not happen.
 */
export function planRoutinePermissionChanges(
  saved: ReadonlyArray<{ key: string; label: string; allowedRoles: readonly string[] }>,
  draft: Readonly<Record<string, readonly string[]>>,
): RoutinePermissionEdit[] {
  const edits: RoutinePermissionEdit[] = [];
  for (const row of saved) {
    const next = draft[row.key];
    if (next === undefined) continue;
    if (sameRoles(row.allowedRoles, next)) continue;
    edits.push({
      key: row.key,
      label: row.label,
      before: normaliseRoles(row.allowedRoles),
      after: normaliseRoles(next),
    });
  }
  return edits;
}

/** Why a draft change is refused. The API enforces these; the UI mirrors them. */
export type RoutineRefusalCode = "empty" | "self_lockout" | "no_holder";

export interface RoutineRefusal {
  key: string;
  code: RoutineRefusalCode;
  /** A whole sentence, rendered verbatim by the screen and by the API's 422. */
  message: string;
}

/** What the refusals are judged against. Both tiers pass the same two facts. */
export interface RoutineRefusalContext {
  /** Every role the person saving currently holds, from `user_role`. */
  actorRoles: readonly UserRole[];
  /** Active users holding each role. A role nobody holds cannot cover a switch. */
  activeHolders: Readonly<Partial<Record<UserRole, number>>>;
}

/**
 * The refusals `decision-application-plan.md` 3.7 asks for, translated onto the
 * surface that exists.
 *
 * 3.7's wording — *"stop an admin removing their own admin or demoting the last
 * one"* — was written for a page that grants and revokes **roles**. That page
 * cannot be built yet: `tb_app` holds SELECT and INSERT on `user_role` and no
 * UPDATE, so nothing in the application can revoke a grant, and there is no
 * controlled setter for one either. The switch board is the surface that does
 * exist, and both protections have an exact analogue on it:
 *
 * - **`self_lockout` — "removing your own admin".** You may not take the last
 *   role *you* hold off a switch. It is the same act: the person making the
 *   change loses the thing they were using to do the work. Unlike the role
 *   version it is recoverable — `set_routine_permission` is gated on the admin
 *   role, not on a switch — but recoverable is not a reason to let someone do it
 *   by accident on the one screen built for it.
 * - **`no_holder` — "demoting the last one".** A switch may not be left listing
 *   only roles no active user holds. The routine stays enforced,
 *   `tb_require_actor_role` still finds the row, and every caller is refused by
 *   a rule naming roles that describe nobody.
 * - **`empty`** mirrors `routine_permission_not_empty_chk`. The database refuses
 *   it as a bare `23514`; this refuses it in a sentence, before the save.
 */
export function refusalsForRoutineEdits(
  edits: readonly RoutinePermissionEdit[],
  context: RoutineRefusalContext,
): RoutineRefusal[] {
  const refusals: RoutineRefusal[] = [];
  for (const edit of edits) {
    const after = normaliseRoles(edit.after);

    if (after.length === 0) {
      refusals.push({
        key: edit.key,
        code: "empty",
        message:
          `"${edit.label}" would be left with no role at all. An empty switch does not mean ` +
          "nobody may do this — it bricks the routine, and the database refuses it outright. " +
          "Take off the roles that should not have it, one at a time.",
      });
      // Both checks below read "who is left", and an empty list makes both of
      // them trivially true. Three refusals for one mistake buries the one the
      // reader can act on.
      continue;
    }

    if (
      context.actorRoles.some((role) => edit.before.includes(role)) &&
      !context.actorRoles.some((role) => after.includes(role))
    ) {
      const held = context.actorRoles.map((role) => userRoleLabels[role]);
      refusals.push({
        key: edit.key,
        code: "self_lockout",
        message:
          `This removes your own access to "${edit.label}". You hold ${held.join(", ")}, and ` +
          `${held.length === 1 ? "it is not" : "none of those is"} left on the switch. ` +
          "Another admin can make this change if it is intended.",
      });
    }

    if (!after.some((role) => (context.activeHolders[role] ?? 0) > 0)) {
      refusals.push({
        key: edit.key,
        code: "no_holder",
        message:
          `Nobody would be able to do "${edit.label}". No active user holds ` +
          `${after.map((role) => userRoleLabels[role]).join(" or ")}, so every attempt would ` +
          "be refused by a rule naming roles that describe no one.",
      });
    }
  }
  return refusals;
}

/**
 * `work_order_stoppage.reason` CHECK (migration 0067).
 *
 * A REAL constraint, unlike `downtimeReasons` above — the column rejects anything
 * outside this list, so a reader never has to tolerate an outsider and the mapper
 * below is a lookup rather than a fallback.
 *
 * The vocabulary IS the feature. MaintainX records an On Hold status and a
 * `Time On Hold` total but never why, and a total with no cause cannot tell you
 * that you are losing days to parts you failed to stock.
 */
export const stoppageReasons = [
  "part_needed",
  "another_fault_found",
  "awaiting_vendor",
  "awaiting_approval",
  "reassigned",
  "shift_end",
  "break",
  "switched_task",
  "other",
] as const;
export type StoppageReason = (typeof stoppageReasons)[number];

/**
 * Labelled because these render in a select on the mechanic's screen, and the
 * stored tokens are not what anyone would choose to read. "Waiting on a part" is
 * the phrase Matt used; `part_needed` is the phrase the CHECK uses.
 */
export const stoppageReasonLabels: Record<StoppageReason, string> = {
  part_needed: "Waiting on a part",
  another_fault_found: "Another fault found",
  awaiting_vendor: "Waiting on vendor",
  awaiting_approval: "Waiting on approval",
  reassigned: "Reassigned",
  shift_end: "End of shift",
  break: "Break / paused",
  switched_task: "Switched to another job",
  other: "Other",
};

export const mechanicSelectableStoppageReasons = stoppageReasons.filter(
  (reason): reason is Exclude<StoppageReason, "switched_task"> => reason !== "switched_task",
);

/**
 * The two reasons that carry EVIDENCE rather than just a label.
 *
 * `part_needed` may name the part that was missing and `another_fault_found` may
 * name the work order it spawned; migration 0067 has a CHECK per arm forbidding
 * each field on any other reason. Exported so a form knows which extra control to
 * show without restating the rule.
 */
export const stoppageReasonEvidence = {
  part_needed: "blockedOnPartId",
  another_fault_found: "spawnedWorkOrderId",
} as const;

/**
 * NOTE ON WORK-ORDER CATEGORIES, and why there is no array for them here.
 *
 * `work_order_category` is a user-managed table (requirement 11.24), not a CHECK,
 * so its values arrive from the database at runtime and cross the wire as a uuid
 * plus `CategoryOption`. Mirroring them here would freeze a list the /config screen
 * exists to let people change, and a category present in the database but absent
 * from a hard-coded array would be silently unusable — the same failure `routineAreaFor`
 * avoids by falling through to `other` instead of filtering.
 *
 * Grouping or ordering categories in code is fine; validating against a fixed list
 * is not.
 *
 * The same applies to `root_cause` and `asset_status_reason` as of TRBUD-115:
 * both are user-managed vocabularies with write routes under /config, so both
 * cross the wire as rows. What IS mirrored below is the pair of CHECK
 * constraints on those tables' fixed columns — `fault_code.default_severity` and
 * `asset_status_reason.availability` — which are closed value sets in the schema
 * and stay closed.
 */

/**
 * `fault_code.default_severity` CHECK, from migration 0004 onwards.
 *
 * Six values, and `unknown` is the column DEFAULT rather than a fallback the UI
 * invents: a fault code landed from a vendor standard usually says nothing about
 * how bad the fault is, and pretending otherwise would put a severity on the
 * screen that nobody asserted.
 *
 * Ordered least-to-most severe with `unknown` LAST, so a picker renders the
 * ladder in the order a person reads it and the non-answer does not sit in the
 * middle of it. Do not sort this array to derive severity — the ordering is a
 * presentation choice, and nothing in the schema ranks these.
 */
export const faultSeverities = ["info", "low", "medium", "high", "critical", "unknown"] as const;
export type FaultSeverity = (typeof faultSeverities)[number];

/** Display labels. `unknown` reads as a statement about the record, not the truck. */
export const faultSeverityLabels: Record<FaultSeverity, string> = {
  info: "Info",
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
  unknown: "Not stated",
};

/**
 * `asset_status_reason.availability` CHECK — two values, and the reason it is a
 * property of the REASON rather than of the event.
 *
 * "In the shop" is always inactive and "on a load" is always active, so holding
 * availability here means the pair cannot disagree: there is no way to record an
 * asset as active and in the shop. See
 * `packages/db/src/schema/core/asset_status.ts`.
 *
 * ── READ THE BOUNDARY BEFORE ADDING A REASON ─────────────────────────
 * This ONE column carries both dispatch reasons (`on_load`, `idle`) and
 * maintenance ones (`in_shop`), and Sylectus's own `Status` field likewise holds
 * `On a Load` / `In Service` / `Out of Service` in one place. Per TRBUD-38 those
 * are TWO AXES that must never collapse into one last-writer-wins status:
 * QuoteBuddy owns dispatch, TruckBuddy owns maintenance downtime. Adding a
 * vocabulary row is configuration and is allowed; adding one in order to assert
 * a dispatch fact from TruckBuddy is the collapse.
 * See docs/dispatch-maintenance-boundary.md.
 */
export const statusReasonAvailabilities = ["active", "inactive"] as const;
export type StatusReasonAvailability = (typeof statusReasonAvailabilities)[number];

/** Display labels for the availability a status reason claims. */
export const statusReasonAvailabilityLabels: Record<StatusReasonAvailability, string> = {
  active: "Available to work",
  inactive: "Not available",
};

/**
 * HOW an asset's home base was decided. Mirrors `asset.domicile_basis`. TRBUD-171.
 *
 * ── An estimate must never read like a stated fact ───────────────────
 *
 * Three of these five are INFERENCES and two are ASSERTIONS, and the whole reason
 * the column exists beside `domicile_location_id` is that a screen rendering them
 * identically invites a manager to schedule a PM at a terminal the unit has never
 * visited. So every readout pairs the place with its basis — "Laredo — estimated
 * from the nearest terminal" is a different claim from "Laredo — our own lot",
 * and the UI is required to say which.
 *
 * `manual` is a human's override and has NO writer today. It is in the vocabulary
 * so the projector can refuse to overwrite it from the first run rather than
 * after somebody's ruling has already been trampled.
 */
export const homeBaseBases = [
  "own_lot",
  "vendor_site",
  "nearest_estimate",
  "unit_suffix",
  "manual",
] as const;
export type HomeBaseBasisValue = (typeof homeBaseBases)[number];

/**
 * The bases a MAP ROW may confer — everything except `manual`.
 *
 * `manual` is one person's ruling about one asset, not a way of deciding a place,
 * and `asset_home_base_map_basis_chk` does not admit it.
 */
export const homeBaseMapBases = ["own_lot", "vendor_site", "nearest_estimate", "unit_suffix"] as const;
export type HomeBaseMapBasis = (typeof homeBaseMapBases)[number];

/** Which shape a mapping row's key is in. Mirrors `asset_home_base_map.match_kind`. */
export const homeBaseMatchKinds = ["site_name", "city", "unit_suffix"] as const;
export type HomeBaseMatchKindValue = (typeof homeBaseMatchKinds)[number];

/** Short labels, for a column heading or a tag. */
export const homeBaseBasisLabels: Record<HomeBaseBasisValue, string> = {
  own_lot: "Our own lot",
  vendor_site: "Vendor site",
  nearest_estimate: "Nearest estimate",
  unit_suffix: "Unit number",
  manual: "Set by a person",
};

/**
 * The phrase that goes after the terminal name — "Laredo — estimated from the
 * nearest terminal".
 *
 * Written as a continuation rather than a sentence so a readout reads naturally,
 * and worded so the two inferred bases cannot be mistaken for the stated ones.
 */
export const homeBaseBasisPhrases: Record<HomeBaseBasisValue, string> = {
  own_lot: "our own lot",
  vendor_site: "nearest terminal to the vendor site it was last left at",
  nearest_estimate: "estimated from the nearest terminal",
  unit_suffix: "from the unit number",
  manual: "set by a person",
};

/** The longer explanation, for a tooltip or a Configuration panel. */
export const homeBaseBasisDescriptions: Record<HomeBaseBasisValue, string> = {
  own_lot:
    "The last drop was one of our own yards, so this is where the asset belongs rather than a guess.",
  vendor_site:
    "The last drop was a vendor's shop. Home base is the nearest CES terminal to it, not the shop.",
  nearest_estimate:
    "Inferred from the city and state alone — the nearest CES terminal. The weakest basis: nobody has said the asset belongs here.",
  unit_suffix:
    "Read off the terminal code in the unit number, such as E-7018-DTW. A statement by whoever numbered the unit.",
  manual: "A person set this by hand. No automatic run will overwrite it.",
};

export const homeBaseMatchKindLabels: Record<HomeBaseMatchKindValue, string> = {
  site_name: "Drop yard name",
  city: "City and state",
  unit_suffix: "Unit number suffix",
};

/** True for the bases that are inferences rather than statements. */
export function homeBaseBasisIsEstimate(basis: HomeBaseBasisValue): boolean {
  return basis === "nearest_estimate" || basis === "vendor_site";
}

/**
 * The work-order queue's four views — TRBUD-216 / TRBUD-223.
 *
 * Eden, in the 9 September 2026 demo: *"If Work Order Complete need it to leave
 * the Queue. Need completed/Cancelled WO tab"* and *"Eventually it will
 * disappear from that queue instead of clogging it up like it currently is."*
 *
 * ── Why `is_completed` is the boundary, and not `is_closed` ──────────
 * `work_order_stage_closed_chk` is `NOT is_closed OR is_completed`, so every
 * closed stage carries `is_completed` and `is_completed` is a strict SUPERSET of
 * `is_closed`. The queue's old boundary was `NOT is_closed`, which therefore kept
 * every stage that is completed-but-not-closed:
 *
 * | pipeline   | stage                     | is_completed | is_closed |
 * | ---------- | ------------------------- | ------------ | --------- |
 * | `in_house` | `repair_complete_testing` | yes          | **no**    |
 * | `vendor`   | `repair_complete`         | yes          | **no**    |
 *
 * Those two are the OPERATIONAL endpoint — the truck is signed off and back on
 * the road, and `is_completed` is what stops the downtime and MTTR clocks. A
 * repair that is finished was staying in the working queue until somebody
 * settled the paperwork, which is the "clogging".
 *
 * It is not only a system-created problem. `REST_STATUS_TO_STAGE.in_house.DONE`
 * is `repair_complete_testing`, and 1731 of the 1769 landed MaintainX rows are
 * `DONE` — so the history import would have put every one of them on the queue.
 *
 * ── `unclosed` is the PRE-CHANGE boundary, kept on purpose ───────────
 * A vendor invoice arrives AFTER `repair_complete`, so the cost and
 * purchase-order pickers must still be able to reach work whose repair is done
 * and whose paperwork is not. They ask for this value explicitly rather than
 * inheriting a default that silently narrowed under them. It is deliberately not
 * offered as a tab: it is a caller's need, not a view of the fleet.
 *
 * `cancelled` is a stage CODE test, not a flag test, for the reason migration
 * `0140` records — a cancellation carries `is_completed` by that same constraint,
 * so neither boolean can tell a void from a finished repair.
 */
export const queueStatusViews = ["open", "completed", "cancelled", "unclosed"] as const;
export type QueueStatusView = (typeof queueStatusViews)[number];

/** The three a person picks between. `unclosed` is for callers, not for a tab. */
export const queueStatusTabs = ["open", "completed", "cancelled"] as const;

export const queueStatusLabels: Record<(typeof queueStatusTabs)[number], string> = {
  open: "Open",
  completed: "Completed",
  cancelled: "Cancelled",
};

/**
 * ── Work-order itemization ────────────────────────────────────────────
 *
 * The code of the CONTAINER work-order type: a visit is a ticket that holds
 * issues and packages and is never worked directly. Mirrored as
 * `VISIT_TYPE_CODE` in `packages/db/src/schema/core/work_order_pipelines.ts`,
 * because neither package may import the other (`boundary.test.ts`; the db
 * package does not depend on the contract). `apps/api/src/visit-type-code-parity.test.ts`
 * keeps the two copies equal, on the `isPhoneShaped` precedent.
 */
export const VISIT_TYPE_CODE = "visit";

/**
 * An issue's three states. `open` is the default; `resolved` carries a clock
 * (`work_order_issue_resolved_chk`); `withdrawn` means it was never fixed and
 * never will be under this ticket. "Unplaced" is NOT a status — it is
 * `package_id IS NULL`, so the two cannot disagree.
 */
export const workOrderIssueStatuses = ["open", "resolved", "withdrawn"] as const;

/**
 * ── The queue's words, defined where people meet them ─────────────────
 *
 * Eden, reviewing the queue on 18 September 2026, asked what "work order",
 * "asset grouping" and "package" mean, then: *"I want those definitions to be
 * somewhere accessible on the site. perhaps as tool tips during work order
 * generation."*
 *
 * ONE source, here, because the words already appear in four places — the band
 * on the queue, the counts strip, the type picker in the New Work Order dialog
 * and the package-routing dialog — and a definition copied into each is four
 * definitions that drift. The screens import these; they never restate them.
 *
 * `short` and `long` follow the `homeBaseBasisPhrases` /
 * `homeBaseBasisDescriptions` precedent: `short` is the one sentence a `title`
 * or a line of helper text can carry, `long` is the entry in the glossary
 * disclosure. Both are needed and neither is derivable from the other — a
 * truncated long reads as a sentence cut off, and a padded short reads as a
 * paragraph inside a tooltip.
 */
export interface GlossaryEntry {
  /** The word as it is printed on screen. */
  term: string;
  /** One sentence, tooltip length. Bounded at 140 characters by the contract test. */
  short: string;
  /** The full definition, for the glossary disclosure. */
  long: string;
}

/** The six words the work-order queue uses. Keys are slugs and are never printed. */
export const queueGlossary = {
  asset_grouping: {
    term: "Asset grouping",
    short:
      "The band with the unit number. Everything open on one asset sits under it — a grouping, not a thing you can open or move.",
    long: 'The band with the unit number on the queue. Everything open on one asset — tractor, trailer, forklift — sits under it, so the shop reads "what does 1023 need" once. A grouping, not a thing you can open or move.',
  },
  work_order: {
    term: "Work order",
    short:
      "One job, in one pipeline, with one worker, its own stage clock and its own time and cost ledgers.",
    long: "One job, in one pipeline (in-house or vendor), with one worker, its own stage clock and its own time and cost ledgers. A standalone work order is the ordinary case: raised on its own, lives on its own.",
  },
  itemized_ticket: {
    term: "Itemized ticket",
    short:
      "The driver's list. It holds issues and nothing else — no stages, no time, no money; its state is read off its packages.",
    long: "The driver's list. It holds issues (line items) and nothing else: no stages of its own, no time, no money. Its state is derived from its packages every time it is read — unrouted, in progress, repair complete, closed.",
  },
  package: {
    term: "Package",
    short:
      "A work order that belongs to a ticket: some of the ticket's issues, to one worker in one pipeline. Time and cost live here.",
    long: 'A work order that belongs to a ticket. It takes one or more of the ticket\'s issues to one worker in one pipeline — "brakes to Dana in-house, lights to the vendor" from one list. Time and cost live on the package.',
  },
  issue: {
    term: "Issue",
    short: "One line on a ticket, placed in at most one package of its own ticket.",
    long: "One line on an itemized ticket — one thing the driver reported. It is placed in at most one package of its own ticket, and nowhere else.",
  },
  repair: {
    term: "Repair",
    short:
      "In the queue's counts, any work order that is actually worked — a standalone or a package.",
    long: 'In the queue\'s counts, any work order that is actually worked: a standalone or a package. "1 ticket, 3 repairs" is one list and three jobs.',
  },
} as const satisfies Record<string, GlossaryEntry>;

export type QueueGlossaryKey = keyof typeof queueGlossary;

/**
 * The glossary in reading order, for a `<dl>`.
 *
 * Declared rather than taken from `Object.keys`, because the order is editorial
 * — grouping, then the two containers, then what they hold, then the counting
 * word — and a key order that happens to read well today is not a decision
 * anybody made.
 */
/**
 * The tooltip for the queue band's counts — "1 ticket, 3 repairs".
 *
 * Composed here rather than in each band, because there are two bands (the
 * mechanic's and the manager's, in different files) and a string assembled
 * twice is the `v_vendor_match_queue` copy-paste in miniature: both would look
 * right and only one would be updated.
 *
 * A tooltip is the cheap half of this — it reaches a laptop and no touch device
 * — so the glossary disclosure on the queue, not this, is what actually carries
 * the definitions.
 */
export const queueCountWordsTitle = `${queueGlossary.itemized_ticket.term}: ${queueGlossary.itemized_ticket.short} ${queueGlossary.repair.term}: ${queueGlossary.repair.short}`;

export const queueGlossaryOrder = [
  "asset_grouping",
  "work_order",
  "itemized_ticket",
  "package",
  "issue",
  "repair",
] as const satisfies readonly QueueGlossaryKey[];
